'use client';

import { Container } from '@/components/layout/container/container';
import { Badge } from '@/components/ui/badge/badge';
import { Button } from '@/components/ui/button/button';
import LottieComponent from '@/components/ui/card-with-image/lottie-component';
import { DecorativeLines } from '@/components/ui/decorative-lines';
import { Image } from '@/components/ui/image/image';
import type { SectionBgVariant } from '@/lib/types/misc';
import { cn } from '@/lib/utils';
import { parseMarkdownLinks } from '@/lib/utils/parse-markdown-links';
import {
  createContext,
  HTMLAttributes,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import styles from './tab-section.module.scss';

type RegisteredGroup = { id: string; title: string; iconUrl?: string };

export type ActiveImageData = {
  url: string | undefined;
  alt?: string;
  aspectRatio?: '1:1' | '4:5' | '16:9';
  fit?: 'contain' | 'cover';
};

export type TabSectionContextType = {
  activeGroupId: string | null;
  registeredGroups: RegisteredGroup[];
  activateGroup: (id: string) => void;
  registerGroup: (id: string, title: string, iconUrl?: string) => void;
  unregisterGroup: (id: string) => void;
  updateGroupMeta: (id: string, title: string, iconUrl?: string) => void;
  activeImageData: ActiveImageData;
  updateActiveImage: (data: ActiveImageData) => void;
};

export const TabSectionContext = createContext<TabSectionContextType | null>(null);

const InlineSvgClient = ({ src }: { src: string }) => {
  const [svg, setSvg] = useState<string | null>(null);

  useEffect(() => {
    fetch(src)
      .then((res) => (res.ok ? res.text() : null))
      .then((text) => {
        if (!text?.includes('<svg')) return;
        const processed = text
          .replace(/<svg([^>]*)>/, (_, attrs: string) => {
            const cleaned = attrs.replace(/\s*width="[^"]*"/, '').replace(/\s*height="[^"]*"/, '');
            return `<svg${cleaned} width="32" height="32">`;
          })
          .replace(/fill="(?!none)[^"]*"/g, 'fill="currentColor"')
          .replace(/stroke="(?!none)[^"]*"/g, 'stroke="currentColor"');
        setSvg(processed);
      })
      .catch(() => null);
  }, [src]);

  if (!svg) return null;
  return <span aria-hidden="true" dangerouslySetInnerHTML={{ __html: svg }} />;
};

export type TabSectionExperienceProps = {
  children?: ReactNode;
  sectionId?: string;
  badge?: string;
  title?: string;

  description?: string;
  ctaLabel?: string;
  ctaUrl?: string;
  backgroundVariant?: SectionBgVariant;
  backgroundImage?: string;
  backgroundColorFrom?: string;
  showDecorativeLines?: boolean;
  showDecorativeDots?: boolean;
  isBackgroundDark?: boolean;
  headerAlignment?: 'left' | 'center' | 'right';
};

export const TabSectionExperience = ({
  children,
  sectionId,
  badge,
  title,

  description,
  ctaLabel,
  ctaUrl,
  backgroundVariant = 'white',
  backgroundImage,
  backgroundColorFrom,
  showDecorativeLines = false,
  showDecorativeDots = false,
  isBackgroundDark = false,
  headerAlignment = 'left',
  className,
  ...props
}: TabSectionExperienceProps & HTMLAttributes<HTMLElement>) => {
  const [activeGroupId, setActiveGroupId] = useState<string | null>(null);
  const [registeredGroups, setRegisteredGroups] = useState<RegisteredGroup[]>([]);
  const [activeImageData, setActiveImageData] = useState<ActiveImageData>({ url: undefined });

  const activeGroupIdRef = useRef<string | null>(null);
  activeGroupIdRef.current = activeGroupId;

  // Stable ref so group-management callbacks stay stable.
  const registeredGroupsRef = useRef<RegisteredGroup[]>([]);

  const activateGroup = useCallback((id: string) => {
    setActiveGroupId(id);
  }, []);

  const registerGroup = useCallback((id: string, title: string, iconUrl?: string) => {
    if (registeredGroupsRef.current.find((g) => g.id === id)) return;
    const isFirst = registeredGroupsRef.current.length === 0;
    registeredGroupsRef.current = [...registeredGroupsRef.current, { id, title, iconUrl }];
    setRegisteredGroups(registeredGroupsRef.current);
    if (isFirst) {
      setActiveGroupId(id);
    }
  }, []);

  const unregisterGroup = useCallback((id: string) => {
    registeredGroupsRef.current = registeredGroupsRef.current.filter((g) => g.id !== id);
    setRegisteredGroups([...registeredGroupsRef.current]);
    if (activeGroupIdRef.current === id) {
      const first = registeredGroupsRef.current[0];
      setActiveGroupId(first?.id ?? null);
    }
  }, []);

  const updateGroupMeta = useCallback((id: string, title: string, iconUrl?: string) => {
    registeredGroupsRef.current = registeredGroupsRef.current.map((g) =>
      g.id === id ? { ...g, title, iconUrl } : g,
    );
    setRegisteredGroups([...registeredGroupsRef.current]);
  }, []);

  const updateActiveImage = useCallback((data: ActiveImageData) => {
    setActiveImageData(data);
  }, []);

  const Tag = 'h2' as const;

  return (
    <TabSectionContext.Provider
      value={{
        activeGroupId,
        registeredGroups,
        activateGroup,
        registerGroup,
        unregisterGroup,
        updateGroupMeta,
        activeImageData,
        updateActiveImage,
      }}
    >
      <section
        id={sectionId}
        className={cn(styles.component, styles[`component--${backgroundVariant}`], className)}
        style={
          backgroundVariant === 'custom' && backgroundColorFrom
            ? { background: backgroundColorFrom }
            : undefined
        }
        {...props}
      >
        {backgroundVariant === 'image' && backgroundImage && (
          <div className={styles.component_background}>
            <Image
              src={backgroundImage}
              alt=""
              className={styles.component_background_image}
              fill={true}
              sizes="100vw"
              style={{ objectFit: 'cover', objectPosition: 'center' }}
            />
          </div>
        )}
        {(showDecorativeLines || showDecorativeDots) && (
          <DecorativeLines
            showLines={showDecorativeLines}
            showDots={showDecorativeDots}
            variant={isBackgroundDark ? 'dark' : 'light'}
          />
        )}
        <Container
          className={cn(
            styles.component_container,
            styles['component_container--no-vertical-padding'],
          )}
        >
          {(badge || title || description || (ctaLabel && ctaUrl)) && (
            <div
              className={cn(
                styles.component_header,
                headerAlignment !== 'left' && styles[`component_header--${headerAlignment}`],
              )}
            >
              {badge && <Badge text={badge} />}
              {(title || description) && (
                <div className={styles.component_header_text}>
                  {title && <Tag>{title}</Tag>}
                  {description && <p>{parseMarkdownLinks(description, styles.component_link)}</p>}
                </div>
              )}
              {ctaLabel && ctaUrl && (
                <div className={styles.component_cta}>
                  <Button href={ctaUrl} withArrow>
                    {ctaLabel}
                  </Button>
                </div>
              )}
            </div>
          )}

          <div className={styles.component_tabs_section}>
            {registeredGroups.length >= 2 && (
              <div className={styles.group_selector} data-count={registeredGroups.length}>
                {registeredGroups.map((group) => (
                  <button
                    key={group.id}
                    className={cn(
                      styles.group_tab,
                      group.id === activeGroupId && styles['group_tab--active'],
                    )}
                    onClick={() => activateGroup(group.id)}
                  >
                    {group.iconUrl && (
                      <span className={styles['group_tab__icon']}>
                        {group.iconUrl.toLowerCase().includes('.svg') ? (
                          <InlineSvgClient src={group.iconUrl} />
                        ) : (
                          <Image
                            src={group.iconUrl}
                            alt=""
                            aria-hidden="true"
                            width={32}
                            height={32}
                          />
                        )}
                      </span>
                    )}
                    <span>{group.title}</span>
                  </button>
                ))}
              </div>
            )}

            <div className={styles.component_body}>
              <div className={styles.component_tabs}>{children}</div>
              {activeImageData.url && (
                <div className={styles.component_image}>
                  {/\.lottie(\?|$)/i.test(activeImageData.url) ? (
                    <LottieComponent src={activeImageData.url} className="h-full w-full" />
                  ) : (
                    <Image
                      src={activeImageData.url}
                      alt={activeImageData.alt ?? ''}
                      width={0}
                      height={0}
                      sizes="100%"
                      style={{
                        display: 'block',
                        width: '100%',
                        height: 'auto',
                        objectFit: activeImageData.fit ?? 'cover',
                        objectPosition: 'center',
                        aspectRatio: activeImageData.aspectRatio
                          ? activeImageData.aspectRatio.replace(':', ' / ')
                          : undefined,
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </Container>
      </section>
    </TabSectionContext.Provider>
  );
};
